import { courseRuntimeUrl } from './courseRuntimeContext';
import { getAuthToken } from '../../utils/auth';
import { dispatchUnauthorizedAuthEvent } from '../../utils/authSession';
import { API_BASE_URL } from '../../config/apiConfig';
import { fetchWithCookieCsrf } from '../csrf';

const STREAM_IDLE_TIMEOUT_MS = 45000;

export const fetchQuestionSummary = async (
  questionId: number,
  customUserPrompt?: string,
  customSystemPrompt?: string,
): Promise<{ summary: string; summary_type: string }> => {
  return new Promise((resolve, reject) => {
    let summaryText = '';
    let settled = false;

    fetchStreamingQuestionSummary(
      questionId,
      undefined,
      (chunk) => {
        summaryText += chunk;
      },
      (meta) => {
        if (settled) {
          return;
        }
        settled = true;
        resolve({ summary: summaryText, summary_type: meta.summary_type });
      },
      (err) => {
        if (settled) {
          return;
        }
        settled = true;
        reject(err);
      },
      customUserPrompt,
      customSystemPrompt,
    );
  });
};

/**
 * Stream a summary from the backend via SSE.
 * Calls onChunk for each text fragment as it arrives.
 * Returns an AbortController so the caller can cancel the stream.
 */
export const fetchStreamingQuestionSummary = (
  questionId: number,
  flowRunId: number | undefined,
  onChunk: (text: string) => void,
  onDone: (meta: { summary_type: string; source_keys: string[] }) => void,
  onError: (err: Error) => void,
  customUserPrompt?: string,
  customSystemPrompt?: string,
): AbortController => {
  const url = `${API_BASE_URL}${courseRuntimeUrl(`/api/questions/${questionId}/summary/stream/`)}`;
  const controller = new AbortController();
  let finished = false;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const resetIdleTimer = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
    }
    idleTimer = setTimeout(() => {
      if (finished) {
        return;
      }
      finished = true;
      controller.abort();
      onError(new Error('Summary stream timed out while waiting for data'));
    }, STREAM_IDLE_TIMEOUT_MS);
  };
  const clearIdleTimer = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };
  // Start watchdog immediately in case headers/body are buffered and fetch() doesn't resolve quickly.
  resetIdleTimer();

  const requestBody: Record<string, string | number> = {};
  if (customUserPrompt) {
    requestBody.custom_user_prompt = customUserPrompt;
  }
  if (customSystemPrompt) {
    requestBody.custom_system_prompt = customSystemPrompt;
  }
  if (flowRunId != null) {
    requestBody.flow_run_id = flowRunId;
  }

  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) {
    headers.Authorization = `Token ${token}`;
  }

  fetchWithCookieCsrf(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
    signal: controller.signal,
    credentials: 'include',
  })
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          dispatchUnauthorizedAuthEvent({
            reason: 'session_expired',
            status: 401,
            source: 'questionSummaryStream',
          });
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('ReadableStream not supported');
      }
      const decoder = new TextDecoder();
      let buffer = '';
      const handleDataLine = (line: string) => {
        resetIdleTimer();
        if (!line.startsWith('data: ')) {
          return;
        }

        const jsonStr = line.slice(6);
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed.done) {
            finished = true;
            clearIdleTimer();
            onDone({ summary_type: parsed.summary_type, source_keys: parsed.source_keys });
            return;
          }

          if (parsed.chunk !== undefined) {
            onChunk(parsed.chunk);
            return;
          }

          if (parsed.error) {
            finished = true;
            clearIdleTimer();
            onError(new Error(parsed.error));
          }
        } catch {
          // Ignore malformed SSE lines
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last potentially incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          handleDataLine(line);
        }
      }

      // Flush any trailing payload not ending with newline.
      const trailing = buffer.trim();
      if (trailing) {
        handleDataLine(trailing);
      }

      if (!finished) {
        clearIdleTimer();
        onError(new Error('Summary stream closed before completion'));
      }
    })
    .catch((err) => {
      clearIdleTimer();
      if (err.name !== 'AbortError') {
        onError(err);
      }
    });

  return controller;
};
